import { createContext, useRef, useState, useCallback, useEffect } from 'react'

import { Icons } from '@clientbase/clientbase-library'
import { Dialog as DialogType } from 'context/DialogContext'
import { InvoiceParams } from 'models/Invoice'
import { NFeParams } from 'models/NFe'
import { PaymentParams } from 'models/Payment'

import { Tag } from 'components/TagsAddInput/TagsAddInput'
import UploadComponent from 'components/UploadComponent'

import { convertTagsStringToArray } from 'utils/tags'

import {
  ModalChargeDetailContextProviderProps,
  ModalChargeDetailProviderState,
  ModalProps
} from './context.interface'

export const NOTIFICATIONS_SUMMARY_ID = 'notificationDetails'

export const ModalChargeDetailContext = createContext(
  {} as ModalChargeDetailProviderState
)

export function ModalChargeDetailProvider({
  children,
  onClose,
  onDuplicate
}: ModalChargeDetailContextProviderProps) {
  const [reload, setReload] = useState(false)

  const [dialogReverse, setDialogReverse] = useState({
    isOpen: false,
    uuid: ''
  })

  const [modalNfDetails, setModalNfDetails] = useState<boolean>(false)

  const [dialog, setDialog] = useState<DialogType>({
    title: '',
    isOpenDialog: false,
    description: 'Estorno de cartão',
    icon: 'info' as Icons,
    action: () => ({}),
    actionLabel: ''
  })
  const [invoice, setInvoice] = useState<InvoiceParams>({
    dueDate: '00-00-0000',
    expirationField: '00-00-0000'
  } as InvoiceParams)

  const [paymentType, setPaymentType] = useState<any>([])
  const [paids, setPaids] = useState<PaymentParams[] | null | undefined>()

  const [nfe, setNfe] = useState<NFeParams>({} as NFeParams)

  const [modal, setModal] = useState<ModalProps>({
    isOpenEditCharge: false,
    isOpenModalRecurrenceDetail: false,
    isOpenDialogAntecipate: false,
    isOpenDialogCancel: false,
    isOpenModalPaid: false,
    isOpenConfirmCancelModal: false,
    formNegativation: false,
    loading: false,
    uuid: '',
    uuidRecurrenceDetail: ''
  })

  const upload = useRef<UploadComponent>()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [notificationsExpanded, setNotificationsExpanded] = useState(false)

  const [tab, setTab] = useState(0)

  // Real-time tag synchronization state
  const [realtimeTags, setRealtimeTags] = useState<Tag[]>([])

  // Update real-time tags when invoice changes
  useEffect(() => {
    if (invoice?.tags) {
      const tags = convertTagsStringToArray(invoice.tags, [])
      setRealtimeTags(tags)
    }
  }, [invoice?.tags])

  // Real-time tag synchronization methods
  const updateRealtimeTags = useCallback((tags: Tag[]) => {
    setRealtimeTags(tags)
  }, [])

  const optimisticTagUpdate = useCallback(
    (tags: Tag[], isOptimistic: boolean) => {
      setRealtimeTags(tags)
      // Could add optimistic state tracking here if needed
      // isOptimistic parameter is intentionally unused for now
      void isOptimistic
    },
    []
  )

  const rollbackTags = useCallback((tags: Tag[]) => {
    setRealtimeTags(tags)
  }, [])

  // O accordion de notificações é controlado por este estado, e não por um ref:
  // o Accordion da clientbase-library é um componente de função sem forwardRef,
  // então um ref nele chega sempre null (React 18) e o botão "Reenviar
  // notificação" não fazia nada. O scroll mira o AccordionSummary pelo id; ele
  // fica no topo e a abertura cresce para baixo, sem deslocar o alvo.
  const scrollToNotifications = () => {
    setNotificationsExpanded(true)
    document.getElementById(NOTIFICATIONS_SUMMARY_ID)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    })
  }

  return (
    <ModalChargeDetailContext.Provider
      value={{
        updateModal: setModal,
        modal,
        invoice,
        updateInvoice: setInvoice,
        anchorEl,
        updateAnchorEl: setAnchorEl,
        paymentType,
        updatePaymentType: setPaymentType,
        paids,
        updatePaids: setPaids,
        dialog,
        updateDialog: setDialog,
        reload,
        updateReload: setReload,
        dialogReverse,
        updateDialogReverse: setDialogReverse,
        tab,
        updateTab: setTab,
        nfe,
        updateNfe: setNfe,
        upload,
        modalNfDetails,
        updateModalNfDetails: setModalNfDetails,
        notificationsExpanded,
        updateNotificationsExpanded: setNotificationsExpanded,
        scrollToNotifications,
        // Real-time tag synchronization
        realtimeTags,
        updateRealtimeTags,
        optimisticTagUpdate,
        rollbackTags,
        onClose,
        onDuplicate
      }}
    >
      {children}
    </ModalChargeDetailContext.Provider>
  )
}
